/**
 * CustomModelCreator Component
 * Modal component for creating custom AI mannequin models
 * Supports both image upload and AI generation modes
 */

import React, { useState, useCallback } from 'react';
import {
  Modal,
  Tabs,
  Form,
  Input,
  Select,
  Upload,
  Button,
  Space,
  Typography,
  message,
  Card,
  Radio,
  Alert,
  Image,
  Tooltip,
} from 'antd';
import {
  UploadOutlined,
  RobotOutlined,
  CheckCircleOutlined,
  InboxOutlined,
} from '@ant-design/icons';
import { OnboardingService, BrandCustomModel } from '../../services/onboardingService';

const { Text } = Typography;
const { TextArea } = Input;
const { Dragger } = Upload;

interface CustomModelCreatorProps {
  visible: boolean;
  brandId: string;
  onClose: () => void;
  onSuccess: (model: BrandCustomModel) => void;
  remainingSlots: number;
}

interface UploadFormValues {
  name: string;
  gender: 'male' | 'female';
  bodyType?: string;
}

interface AIFormValues {
  gender: 'male' | 'female';
  ageRange: string;
  skinTone: string;
  hairColor: string;
  upperBody: string;
  lowerBody: string;
  bodyWeight: string;
}

const SKIN_TONE_OPTIONS = [
  { value: 'very fair, porcelain', label: 'Çok Açık', color: '#FFEFD5' },
  { value: 'fair, light', label: 'Açık', color: '#FFE4C4' },
  { value: 'light, cream', label: 'Krem', color: '#F5DEB3' },
  { value: 'medium, olive', label: 'Orta / Zeytin', color: '#DEB887' },
  { value: 'tan, golden', label: 'Bronz', color: '#D2A679' },
  { value: 'medium brown, caramel', label: 'Karamel', color: '#C68642' },
  { value: 'dark brown, chocolate', label: 'Koyu Kahve', color: '#8B4513' },
  { value: 'deep brown, espresso', label: 'Espresso', color: '#5D3A1A' },
  { value: 'very dark, ebony', label: 'Abanoz', color: '#3D2314' },
];

const BODY_WEIGHT_OPTIONS = [
  { value: 'slim build', label: 'İnce' },
  { value: 'fit athletic build', label: 'Fit / Atletik' },
  { value: 'average body type', label: 'Normal' },
  { value: 'curvy build', label: 'Dolgun' },
  { value: 'plus-size build', label: 'Büyük Beden' },
];

const AGE_RANGE_OPTIONS = [
  { value: 'child, 6-10 years old', label: 'Çocuk (6-10 yaş)' },
  { value: 'teenager, 13-17 years old', label: 'Genç (13-17 yaş)' },
  { value: 'young adult, 18-25 years old', label: 'Genç Yetişkin (18-25 yaş)' },
  { value: 'adult, 26-35 years old', label: 'Yetişkin (26-35 yaş)' },
  { value: 'middle-aged, 36-50 years old', label: 'Orta Yaş (36-50 yaş)' },
  { value: 'mature adult, 51-65 years old', label: 'Olgun (51-65 yaş)' },
  { value: 'senior, 65+ years old', label: 'Yaşlı (65+ yaş)' },
];

const HAIR_COLOR_OPTIONS = [
  { value: 'black', label: 'Siyah' },
  { value: 'dark brown', label: 'Koyu Kahverengi' },
  { value: 'light brown', label: 'Açık Kahverengi' },
  { value: 'blonde', label: 'Sarı' },
  { value: 'platinum blonde', label: 'Platin Sarı' },
  { value: 'red', label: 'Kızıl' },
  { value: 'auburn', label: 'Kumral' },
  { value: 'gray', label: 'Gri' },
  { value: 'white', label: 'Beyaz' },
  { value: 'pink', label: 'Pembe' },
  { value: 'blue', label: 'Mavi' },
  { value: 'purple', label: 'Mor' },
  { value: 'green', label: 'Yeşil' },
  { value: 'rainbow colored', label: 'Gökkuşağı' },
  { value: 'ombre pink and purple', label: 'Ombre (Pembe-Mor)' },
  { value: 'silver', label: 'Gümüş' },
  { value: 'rose gold', label: 'Rose Gold' },
];

const CustomModelCreator: React.FC<CustomModelCreatorProps> = ({
  visible,
  brandId,
  onClose,
  onSuccess,
  remainingSlots,
}) => {
  const [activeTab, setActiveTab] = useState<'upload' | 'ai'>('upload');
  const [uploadForm] = Form.useForm<UploadFormValues>();
  const [aiForm] = Form.useForm<AIFormValues>();
  
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [confirming, setConfirming] = useState(false);
  
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [previewPromptConfig, setPreviewPromptConfig] = useState<any>(null);
  const [previewGender, setPreviewGender] = useState<'male' | 'female'>('female');
  const [confirmName, setConfirmName] = useState('');
  const [selectedSkinTone, setSelectedSkinTone] = useState<string | undefined>(undefined);

  const handleClose = useCallback(() => {
    uploadForm.resetFields();
    aiForm.resetFields();
    setUploadedImage(null);
    setPreviewImage(null);
    setPreviewPromptConfig(null);
    setConfirmName('');
    setSelectedSkinTone(undefined);
    onClose();
  }, [uploadForm, aiForm, onClose]);

  const handleImageUpload = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      setUploadedImage(e.target?.result as string);
    };
    reader.readAsDataURL(file);
    return false;
  }, []);

  const handleUploadSubmit = async (values: UploadFormValues) => {
    if (!uploadedImage) {
      message.error('Lütfen bir görsel yükleyin');
      return;
    }

    try {
      setUploading(true);
      const model = await OnboardingService.uploadCustomModel(brandId, {
        name: values.name,
        gender: values.gender,
        image: uploadedImage,
        bodyType: values.bodyType,
      });
      message.success('Model başarıyla oluşturuldu');
      onSuccess(model);
      handleClose();
    } catch (error: any) {
      message.error(error.message || 'Model oluşturulurken bir hata oluştu');
    } finally {
      setUploading(false);
    }
  };

  const handleGeneratePreview = async (values: AIFormValues) => {
    try {
      setGenerating(true);
      setPreviewGender(values.gender);
      
      const skinToneValue = selectedSkinTone || values.skinTone;
      console.log('🎨 Generating preview with skinTone:', skinToneValue, 'values.skinTone:', values.skinTone, 'selectedSkinTone:', selectedSkinTone);
      
      const result = await OnboardingService.generateCustomModelPreview(brandId, {
        gender: values.gender,
        ageRange: values.ageRange,
        skinTone: skinToneValue,
        hairColor: values.hairColor,
        upperBody: values.upperBody,
        lowerBody: values.lowerBody,
        bodyWeight: values.bodyWeight,
      });
      
      setPreviewImage(result.imageUrl);
      setPreviewPromptConfig(result.promptConfig);
      message.success('Önizleme oluşturuldu. Beğendiyseniz isim vererek kaydedin.');
    } catch (error: any) {
      message.error(error.message || 'Önizleme oluşturulurken bir hata oluştu');
    } finally {
      setGenerating(false);
    }
  };

  const handleConfirmAIModel = async () => {
    if (!confirmName.trim()) {
      message.error('Lütfen model için bir isim girin');
      return;
    }

    if (!previewImage) {
      message.error('Önce bir önizleme oluşturun');
      return;
    }

    try {
      setConfirming(true);
      const model = await OnboardingService.confirmCustomModel(brandId, {
        name: confirmName.trim(),
        previewImageUrl: previewImage,
        gender: previewGender,
        promptConfig: previewPromptConfig,
        bodyType: previewPromptConfig?.bodyWeight,
      });
      message.success('Model başarıyla kaydedildi');
      onSuccess(model);
      handleClose();
    } catch (error: any) {
      message.error(error.message || 'Model kaydedilirken bir hata oluştu');
    } finally {
      setConfirming(false);
    }
  };

  const renderUploadTab = () => (
    <Form
      form={uploadForm}
      layout="vertical"
      onFinish={handleUploadSubmit}
      initialValues={{ gender: 'female' }}
    >
      <Form.Item
        name="name"
        label="Model İsmi"
        rules={[{ required: true, message: 'Model ismi gerekli' }]}
      >
        <Input placeholder="Örn: Ayşe, Model 1" maxLength={100} />
      </Form.Item>

      <Form.Item
        name="gender"
        label="Cinsiyet"
        rules={[{ required: true }]}
      >
        <Radio.Group>
          <Radio.Button value="female">Kadın</Radio.Button>
          <Radio.Button value="male">Erkek</Radio.Button>
        </Radio.Group>
      </Form.Item>

      <Form.Item
        name="bodyType"
        label="Vücut Tipi (Opsiyonel)"
      >
        <Select placeholder="Seçiniz" allowClear>
          {BODY_WEIGHT_OPTIONS.map(opt => (
            <Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item label="Model Görseli" required>
        <Dragger
          accept="image/*"
          showUploadList={false}
          beforeUpload={handleImageUpload}
          disabled={uploading}
        >
          {uploadedImage ? (
            <div style={{ padding: 16 }}>
              <img
                src={uploadedImage}
                alt="Preview"
                style={{ maxHeight: 200, maxWidth: '100%', objectFit: 'contain' }}
              />
              <p style={{ marginTop: 8 }}>
                <Text type="secondary">Değiştirmek için tıklayın</Text>
              </p>
            </div>
          ) : (
            <div style={{ padding: 24 }}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Görsel yüklemek için tıklayın veya sürükleyin</p>
              <p className="ant-upload-hint">PNG, JPG formatları desteklenir</p>
            </div>
          )}
        </Dragger>
      </Form.Item>

      <Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          loading={uploading}
          disabled={!uploadedImage}
          icon={<UploadOutlined />}
          block
        >
          Modeli Kaydet
        </Button>
      </Form.Item>
    </Form>
  );

  const renderAITab = () => (
    <div>
      {!previewImage ? (
        <Form
          form={aiForm}
          layout="vertical"
          onFinish={handleGeneratePreview}
          initialValues={{ gender: 'female' }}
        >
          <Form.Item
            name="gender"
            label="Cinsiyet"
            rules={[{ required: true }]}
          >
            <Radio.Group>
              <Radio.Button value="female">Kadın</Radio.Button>
              <Radio.Button value="male">Erkek</Radio.Button>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            name="ageRange"
            label="Yaş Aralığı"
          >
            <Select placeholder="Seçiniz" allowClear>
              {AGE_RANGE_OPTIONS.map(opt => (
                <Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="skinTone"
            label="Ten Rengi"
          >
            <Radio.Group 
              style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}
              onChange={(e) => {
                setSelectedSkinTone(e.target.value);
                aiForm.setFieldsValue({ skinTone: e.target.value });
              }}
            >
              {SKIN_TONE_OPTIONS.map(opt => {
                const isSelected = selectedSkinTone === opt.value;
                return (
                  <Tooltip title={opt.label} key={opt.value}>
                    <Radio.Button
                      value={opt.value}
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: opt.color,
                        borderColor: isSelected ? '#1890ff' : opt.color,
                        borderWidth: isSelected ? 3 : 1,
                        boxShadow: isSelected ? '0 0 0 2px rgba(24, 144, 255, 0.3)' : 'none',
                        transform: isSelected ? 'scale(1.1)' : 'scale(1)',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <span style={{ display: 'none' }}>{opt.label}</span>
                    </Radio.Button>
                  </Tooltip>
                );
              })}
            </Radio.Group>
          </Form.Item>

          <Form.Item
            name="hairColor"
            label="Saç Rengi"
          >
            <Select placeholder="Seçiniz veya yazınız" allowClear showSearch>
              {HAIR_COLOR_OPTIONS.map(opt => (
                <Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="upperBody"
            label="Üst Vücut Özellikleri"
            extra="Örn: geniş omuzlar, atletik üst vücut, ince bel"
          >
            <TextArea
              placeholder="Üst vücut için özellikler yazın..."
              rows={2}
              maxLength={200}
            />
          </Form.Item>

          <Form.Item
            name="lowerBody"
            label="Alt Vücut Özellikleri"
            extra="Örn: uzun bacaklar, geniş kalça, normal alt vücut"
          >
            <TextArea
              placeholder="Alt vücut için özellikler yazın..."
              rows={2}
              maxLength={200}
            />
          </Form.Item>

          <Form.Item
            name="bodyWeight"
            label="Vücut Tipi / Kilo"
          >
            <Select placeholder="Seçiniz">
              {BODY_WEIGHT_OPTIONS.map(opt => (
                <Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Alert
            message="AI ile model oluşturma işlemi 1-2 dakika sürebilir"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={generating}
              icon={<RobotOutlined />}
              block
            >
              {generating ? 'Model Oluşturuluyor...' : 'Önizleme Oluştur'}
            </Button>
          </Form.Item>
        </Form>
      ) : (
        <div>
          <Card style={{ marginBottom: 16 }}>
            <div style={{ textAlign: 'center' }}>
              <Image
                src={previewImage}
                alt="AI Generated Model"
                style={{ maxHeight: 400, objectFit: 'contain' }}
              />
            </div>
          </Card>

          <Alert
            message="Model önizlemesi oluşturuldu"
            description="Beğendiyseniz aşağıya isim girerek kaydedin. Beğenmediyseniz 'Yeniden Oluştur' butonuna tıklayın."
            type="success"
            showIcon
            icon={<CheckCircleOutlined />}
            style={{ marginBottom: 16 }}
          />

          <Form.Item label="Model İsmi" required>
            <Input
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              placeholder="Örn: AI Model 1, Sophia"
              maxLength={100}
            />
          </Form.Item>

          <Space style={{ width: '100%' }} direction="vertical">
            <Button
              type="primary"
              onClick={handleConfirmAIModel}
              loading={confirming}
              disabled={!confirmName.trim()}
              icon={<CheckCircleOutlined />}
              block
            >
              Onayla ve Kaydet
            </Button>
            <Button
              onClick={() => {
                setPreviewImage(null);
                setPreviewPromptConfig(null);
                setConfirmName('');
              }}
              block
            >
              Yeniden Oluştur
            </Button>
          </Space>
        </div>
      )}
    </div>
  );

  return (
    <Modal
      title={
        <Space>
          <RobotOutlined />
          <span>Yeni Model Oluştur</span>
        </Space>
      }
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={600}
      destroyOnClose
    >
      {remainingSlots <= 0 ? (
        <Alert
          message="Model Limiti Doldu"
          description="Maksimum 10 özel model oluşturabilirsiniz. Yeni model eklemek için mevcut bir modeli silin."
          type="warning"
          showIcon
        />
      ) : (
        <>
          <Alert
            message={`Kalan model hakkı: ${remainingSlots}/10`}
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Tabs
            activeKey={activeTab}
            onChange={(key) => setActiveTab(key as 'upload' | 'ai')}
            items={[
              {
                key: 'upload',
                label: (
                  <span>
                    <UploadOutlined /> Görsel Yükle
                  </span>
                ),
                children: renderUploadTab(),
              },
              {
                key: 'ai',
                label: (
                  <span>
                    <RobotOutlined /> AI ile Oluştur
                  </span>
                ),
                children: renderAITab(),
              },
            ]}
          />
        </>
      )}
    </Modal>
  );
};

export default CustomModelCreator;
